/*
 * Luxe & Gloss product state and form controller.
 * Pure helpers are exported in Node so cart payload and recovery behavior can
 * be tested without a browser.
 */
(function (root, factory) {
  const api = factory(root)

  if (typeof module === 'object' && module.exports) module.exports = api
  if (root) {
    root.LG = root.LG || {}
    root.LG.product = api
  }
})(typeof window !== 'undefined' ? window : undefined, function (root) {
  'use strict'

  class ProductRequestError extends Error {
    constructor (code, message, options = {}) {
      super(message)
      this.name = 'ProductRequestError'
      this.code = code
      this.status = options.status || 0
      this.recoverable = options.recoverable !== false
      this.cause = options.cause
    }
  }

  const positiveInteger = (value, fallback = 1) => {
    const number = Number.parseInt(value, 10)
    return Number.isFinite(number) && number > 0 ? number : fallback
  }

  const serializeLineItem = (entries) => {
    const values = Array.from(entries || [])
    const read = (name) => values.find(([key]) => key === name)?.[1]
    const idValue = read('id')
    const id = /^\d+$/.test(String(idValue || '')) ? Number(idValue) : idValue

    if (!id) {
      throw new ProductRequestError('VARIANT_REQUIRED', 'Choose an available option before adding this set.')
    }

    const item = {
      id,
      quantity: positiveInteger(read('quantity'))
    }
    const properties = {}

    values.forEach(([name, value]) => {
      const match = String(name).match(/^properties\[(.+)]$/)
      if (!match || value === '' || value == null) return
      properties[match[1]] = String(value)
    })

    if (Object.keys(properties).length) item.properties = properties

    const sellingPlan = read('selling_plan')
    if (sellingPlan) {
      item.selling_plan = /^\d+$/.test(String(sellingPlan)) ? Number(sellingPlan) : sellingPlan
    }

    return item
  }

  const resolveVariant = (variants, selectedOptions) => {
    if (!Array.isArray(variants) || !variants.length) return null
    if (!selectedOptions.length && variants.length === 1) return variants[0]

    return variants.find(variant =>
      Array.isArray(variant.options) &&
      variant.options.length === selectedOptions.length &&
      variant.options.every((option, index) => option === selectedOptions[index])
    ) || null
  }

  const pickInitialVariant = (variants, inputId, search = '') => {
    if (!Array.isArray(variants) || !variants.length) return null
    const directId = new URLSearchParams(search).get('variant')
    const requestedId = directId || inputId
    return variants.find(variant => String(variant.id) === String(requestedId)) ||
      variants.find(variant => String(variant.id) === String(inputId)) ||
      null
  }

  const normalizeProperties = (properties) => Object.entries(properties || {})
    .filter(([, value]) => value != null && value !== '')
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => `${key}:${String(value)}`)
    .join('|')

  const cleanIdentity = value => String(value ?? '').trim()

  const fitIdentityMatches = (detail, context) => {
    if (!detail || !context || detail.version !== 1) return false
    const expectedProductId = cleanIdentity(context.productId)
    const expectedSystemId = cleanIdentity(context.fitSystemId)
    const expectedSystemVersion = cleanIdentity(context.fitSystemVersion)
    if (!expectedProductId || !expectedSystemId || !expectedSystemVersion) return false

    return cleanIdentity(detail.productId) === expectedProductId &&
      cleanIdentity(detail.fitSystemId) === expectedSystemId &&
      cleanIdentity(detail.fitSystemVersion) === expectedSystemVersion
  }

  const removeMatchingFitProperties = (form, detail) => {
    if (!form?.querySelectorAll) return 0
    const systemId = cleanIdentity(detail?.fitSystemId)
    const systemVersion = cleanIdentity(detail?.fitSystemVersion)
    if (!systemId || !systemVersion) return 0
    let removed = 0

    form.querySelectorAll('[data-lg-fit-property]').forEach(input => {
      if (cleanIdentity(input.dataset.lgFitSystemId) !== systemId ||
        cleanIdentity(input.dataset.lgFitSystemVersion) !== systemVersion) return
      input.remove()
      removed += 1
    })
    return removed
  }

  const cartLineQuantity = (cart, item) => {
    if (!cart || !Array.isArray(cart.items)) return 0
    return cart.items.reduce((total, line) => {
      const linePlan = line.selling_plan_allocation?.selling_plan?.id || line.selling_plan || ''
      const sameVariant = String(line.variant_id || line.id) === String(item.id)
      const sameProperties = normalizeProperties(line.properties) === normalizeProperties(item.properties)
      const samePlan = String(linePlan) === String(item.selling_plan || '')
      return sameVariant && sameProperties && samePlan ? total + Number(line.quantity || 0) : total
    }, 0)
  }

  const parseResponse = async (response) => {
    try {
      return await response.json()
    } catch (error) {
      return {}
    }
  }

  const withJsonSuffix = (route) => {
    const value = route || '/cart'
    return value.endsWith('.js') ? value : `${value}.js`
  }

  const requestCart = async (fetchImpl, cartRoute) => {
    const response = await fetchImpl(withJsonSuffix(cartRoute), {
      headers: { Accept: 'application/json' }
    })
    const body = await parseResponse(response)
    if (!response.ok) {
      throw new ProductRequestError(`CART_${response.status}`, 'We could not confirm your bag. Please open it before retrying.', {
        status: response.status,
        recoverable: false
      })
    }
    return body
  }

  const responseError = (response, body) => {
    const message = body.description || body.message ||
      (response.status === 422
        ? 'That selection is unavailable. Review your options and try again.'
        : 'We could not add this set. Please check your bag before retrying.')
    return new ProductRequestError(`CART_${response.status}`, message, {
      status: response.status,
      recoverable: response.status < 500
    })
  }

  /**
   * Compatibility transport used only until window.LG.cart is available.
   * It compares server cart state before/after ambiguous failures and never
   * automatically retries an add request.
   */
  const fallbackAdd = async ({ item, fetchImpl, routes = {}, pathname = '/' }) => {
    const fetcher = fetchImpl || root?.fetch?.bind(root)
    if (!fetcher) throw new ProductRequestError('NETWORK', 'You appear to be offline. Reconnect and try again.')

    const addRoute = withJsonSuffix(routes.cart_add_url || '/cart/add')
    const cartRoute = routes.cart_url || '/cart'
    let cartBefore = null

    try {
      cartBefore = await requestCart(fetcher, cartRoute)
    } catch (error) {
      // The add may still be attempted, but an ambiguous result cannot be
      // called a success when the original server state was unavailable.
    }

    const payload = {
      items: [item],
      sections: ['cart-count-badge', 'offcanvas-cart'],
      sections_url: pathname
    }

    let response
    let body = {}
    try {
      response = await fetcher(addRoute, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })
      body = await parseResponse(response)
    } catch (error) {
      if (cartBefore) {
        try {
          const cart = await requestCart(fetcher, cartRoute)
          if (cartLineQuantity(cart, item) >= cartLineQuantity(cartBefore, item) + item.quantity) {
            return { cart, sections: null, reconciled: true }
          }
        } catch (reconcileError) {}
      }
      throw new ProductRequestError('NETWORK', 'The connection was interrupted. Check your bag before retrying.', {
        recoverable: false,
        cause: error
      })
    }

    if (!response.ok) {
      if (response.status >= 500 && cartBefore) {
        try {
          const cart = await requestCart(fetcher, cartRoute)
          if (cartLineQuantity(cart, item) >= cartLineQuantity(cartBefore, item) + item.quantity) {
            return { cart, sections: body.sections || null, reconciled: true }
          }
        } catch (reconcileError) {}
      }
      throw responseError(response, body)
    }

    let cart
    try {
      cart = await requestCart(fetcher, cartRoute)
    } catch (error) {
      throw new ProductRequestError('CART_UNCONFIRMED', 'The set may have been added. Open your bag to confirm before retrying.', {
        recoverable: false,
        cause: error
      })
    }

    if (cartBefore && cartLineQuantity(cart, item) < cartLineQuantity(cartBefore, item) + item.quantity) {
      throw new ProductRequestError('CART_UNCONFIRMED', 'We could not confirm the selected set in your bag. Review your bag before retrying.', {
        recoverable: false
      })
    }

    return { cart, sections: body.sections || null, reconciled: false }
  }

  const productLocales = () => root?.theme?.locales?.product || {}

  const formatMoney = (cents) => {
    if (root?.Shopify?.formatMoney) return root.Shopify.formatMoney(cents)
    return (Number(cents || 0) / 100).toFixed(2)
  }

  const defineBrowserElements = () => {
    if (!root || typeof root.HTMLElement === 'undefined' || !root.customElements) return

    class ProductForm extends root.HTMLElement {
      connectedCallback () {
        if (this.initialized) return
        this.initialized = true
        this.form = this.querySelector('form')
        if (!this.form) return

        this.productOptions = Array.from(this.form.querySelectorAll('[data-product-option], .product-options [name^="option-"], .pd-option-btns input[name^="options["]'))
        this.atcBtn = this.form.querySelector('[data-lg-atc], button[type="submit"][name="add"], .btn-atc, .pd-atc')
        this.buyBtn = this.form.querySelector('.btn-buy')
        this.variantInput = this.form.querySelector('[name="id"]')
        this.errorEl = this.querySelector('[data-lg-product-error]')
        this.statusEl = this.querySelector('[data-lg-product-status]')
        this.variants = this.readVariants()
        this.abortController = new AbortController()
        const signal = this.abortController.signal

        this.form.addEventListener('submit', event => this.onSubmit(event), { signal })
        this.productOptions.forEach(element => {
          element.addEventListener('change', event => this.onChangeProductOption(event), { signal })
        })
        this.form.querySelectorAll('[data-lg-quantity-change]').forEach(button => {
          button.addEventListener('click', () => this.changeQuantity(button), { signal })
        })
        this.buyBtn?.addEventListener('click', event => this.onClickBuyBtn(event), { signal })
        root.addEventListener('lg:fit:confirmed', event => this.onFitConfirmed(event), { signal })
        root.addEventListener('lg:fit:invalidated', event => this.onFitInvalidated(event), { signal })

        const initialVariant = pickInitialVariant(
          this.variants,
          this.variantInput?.value,
          this.closest('[data-lg-main-product]') ? root.location.search : ''
        )
        if (initialVariant) this.selectVariantOptions(initialVariant)
        this.updateVariant(initialVariant || this.variantFromOptions(), { updateUrl: false, announce: false })
      }

      disconnectedCallback () {
        this.abortController?.abort()
        this.initialized = false
      }

      readVariants () {
        try {
          return JSON.parse(this.form.dataset.variants || '[]')
        } catch (error) {
          return []
        }
      }

      selectedOptions () {
        const groups = []
        this.productOptions.forEach(element => {
          if ((element.type === 'radio' || element.type === 'checkbox') && !element.checked) return
          const index = Number(element.dataset.optionPosition || element.closest('[data-option-position]')?.dataset.optionPosition)
          if (Number.isInteger(index)) groups[index] = element.value
        })
        return groups.filter(value => value !== undefined)
      }

      variantFromOptions () {
        if (!this.variants.length) return null
        return resolveVariant(this.variants, this.selectedOptions())
      }

      selectVariantOptions (variant) {
        if (!variant?.options) return
        this.productOptions.forEach(element => {
          const position = Number(element.dataset.optionPosition || element.closest('[data-option-position]')?.dataset.optionPosition)
          if (!Number.isInteger(position)) return
          if (element.type === 'radio') element.checked = element.value === variant.options[position]
          if (element.tagName === 'SELECT') element.value = variant.options[position]
        })
      }

      onChangeProductOption (event) {
        event.preventDefault()
        this.updateVariant(this.variantFromOptions(), { updateUrl: true, announce: true })
      }

      updateVariant (variant, options = {}) {
        this.currentVariant = variant
        if (this.variantInput) this.variantInput.value = variant?.id || ''
        this.adjustPricing(variant)
        this.adjustButtons(variant)

        if (variant && options.updateUrl && this.closest('[data-lg-main-product]')) {
          const url = new URL(root.location.href)
          url.searchParams.set('variant', variant.id)
          root.history.replaceState({}, '', url)
        }

        if (options.announce && this.statusEl) {
          this.statusEl.textContent = variant
            ? (variant.available ? 'Selection available.' : 'This selection is sold out.')
            : 'This option combination is unavailable.'
        }

        const detail = {
          version: 1,
          productId: Number(this.form.dataset.productId || this.dataset.productId || 0),
          variantId: variant?.id || null,
          available: Boolean(variant?.available),
          options: variant?.options || this.selectedOptions(),
          sectionId: this.form.dataset.sectionId || this.dataset.sectionId || ''
        }
        root.dispatchEvent(new root.CustomEvent('lg:variant:changed', { detail }))
        root.dispatchEvent(new root.CustomEvent('kt.product.variant.change', { detail: variant }))
      }

      adjustPricing (variant) {
        const comparePrice = this.querySelector('[data-lg-price-compare], .product-price-compare')
        const finalPrice = this.querySelector('[data-lg-price-current], .product-price-final')
        const compareBadge = this.querySelector('[data-lg-sale-badge], .product-sale-badge')
        const atcPrice = this.querySelector('[data-lg-atc-price], .pd-atc-price')
        if (!variant) {
          if (finalPrice) finalPrice.textContent = productLocales().unavailable || 'Unavailable'
          if (atcPrice) atcPrice.textContent = ''
          if (comparePrice) comparePrice.setAttribute('hidden', '')
          if (compareBadge) compareBadge.setAttribute('hidden', '')
          return
        }
        const onSale = Number(variant.compare_at_price) > Number(variant.price)

        if (finalPrice) finalPrice.textContent = formatMoney(variant.price)
        if (atcPrice) atcPrice.textContent = formatMoney(variant.price)
        if (comparePrice) {
          comparePrice.textContent = onSale ? formatMoney(variant.compare_at_price) : ''
          comparePrice.toggleAttribute('hidden', !onSale)
        }
        if (compareBadge) {
          compareBadge.textContent = onSale ? `${productLocales().save || 'Save'} ${formatMoney(variant.compare_at_price - variant.price)}` : ''
          compareBadge.toggleAttribute('hidden', !onSale)
        }
      }

      adjustButtons (variant) {
        if (!this.atcBtn || this.busy) return
        const available = this.variants.length ? Boolean(variant?.available) : Boolean(this.variantInput?.value)
        const label = this.atcBtn.querySelector('[data-lg-atc-label]')
        this.atcBtn.disabled = !available
        if (this.buyBtn) this.buyBtn.disabled = !available
        if (label) {
          label.textContent = variant
            ? (variant.available ? (productLocales().add_to_cart || 'Add to cart') : (productLocales().sold_out || 'Sold out'))
            : (productLocales().unavailable || 'Unavailable')
        } else {
          this.atcBtn.textContent = variant
            ? (variant.available ? (productLocales().add_to_cart || 'Add to cart') : (productLocales().sold_out || 'Sold out'))
            : (productLocales().unavailable || 'Unavailable')
        }
      }

      setBusy (busy) {
        this.busy = busy
        if (!this.atcBtn) return
        this.atcBtn.classList.toggle('loading', busy)
        this.atcBtn.setAttribute('aria-busy', String(busy))
        if (busy) this.atcBtn.disabled = true
        else this.adjustButtons(this.currentVariant)
      }

      showError (message) {
        if (!this.errorEl) {
          this.errorEl = document.createElement('p')
          this.errorEl.className = 'product-form-error'
          this.errorEl.setAttribute('role', 'alert')
          this.form.append(this.errorEl)
        }
        this.errorEl.textContent = message
        this.errorEl.removeAttribute('hidden')
      }

      clearFeedback () {
        if (this.errorEl) {
          this.errorEl.textContent = ''
          this.errorEl.setAttribute('hidden', '')
        }
        if (this.statusEl) this.statusEl.textContent = ''
      }

      async onSubmit (event) {
        event.preventDefault()
        if (this.busy) return
        this.clearFeedback()

        let item
        try {
          item = serializeLineItem(new root.FormData(this.form).entries())
          if (this.variants.length && !this.currentVariant?.available) {
            throw new ProductRequestError('VARIANT_UNAVAILABLE', 'Choose an available option before adding this set.')
          }
        } catch (error) {
          this.showError(error.message)
          this.adjustButtons(this.currentVariant)
          return
        }

        this.setBusy(true)
        try {
          if (root.LG?.cart?.version === 1 && typeof root.LG.cart.add === 'function') {
            await root.LG.cart.add({ items: [item], source: 'product' })
          } else {
            const result = await fallbackAdd({
              item,
              fetchImpl: root.fetch.bind(root),
              routes: root.Shopify?.routes || {},
              pathname: root.location.pathname
            })
            this.refreshLegacyCart(result.sections)
            root.dispatchEvent(new root.CustomEvent('kt.cart.added', { detail: result.cart }))
          }
          if (this.statusEl) this.statusEl.textContent = 'Added to your bag.'
        } catch (error) {
          const message = error?.message || 'We could not add this set. Please try again.'
          this.showError(message)
        } finally {
          this.setBusy(false)
        }
      }

      refreshLegacyCart (sections) {
        if (!sections) return
        const cart = document.querySelector('cart-container')
        if (cart?.reloadCartElements) cart.reloadCartElements(sections)
      }

      openLegacyCart () {
        const drawer = document.querySelector('#offcanvas-cart')
        if (drawer && root.bootstrap?.Offcanvas) {
          root.bootstrap.Offcanvas.getOrCreateInstance(drawer).show()
        }
      }

      onClickBuyBtn (event) {
        // Legacy buttons cannot safely serialize line properties or plans.
        // Submit through the normal product form, then let the cart own checkout.
        event.preventDefault()
        this.form.requestSubmit(this.atcBtn)
      }

      changeQuantity (button) {
        const input = this.form.querySelector('[name="quantity"]')
        if (!input) return
        const change = Number(button.dataset.lgQuantityChange || 0)
        const minimum = positiveInteger(input.min, 1)
        input.value = Math.max(minimum, positiveInteger(input.value) + change)
        input.dispatchEvent(new Event('change', { bubbles: true }))
      }

      hostedFitIdentity () {
        const host = this.closest('[data-lg-main-product]')
        const panel = host?.querySelector('[data-lg-fit-panel-slot] lg-fit-panel')
        return {
          productId: this.form.dataset.productId || this.dataset.productId || host?.dataset.productId,
          fitSystemId: panel?.dataset.fitSystemId,
          fitSystemVersion: panel?.dataset.fitSystemVersion
        }
      }

      onFitConfirmed (event) {
        const detail = event.detail || {}
        if (!fitIdentityMatches(detail, this.hostedFitIdentity())) return

        const recommended = detail.recommendedVariantId == null
          ? null
          : this.variants.find(variant => String(variant.id) === String(detail.recommendedVariantId))
        if (detail.recommendedVariantId != null && !recommended?.available) return

        removeMatchingFitProperties(this.form, detail)
        Object.entries(detail.properties || {}).forEach(([name, value]) => {
          if (value == null || value === '') return
          const input = document.createElement('input')
          input.type = 'hidden'
          input.name = `properties[${name}]`
          input.value = String(value)
          input.dataset.lgFitProperty = 'true'
          input.dataset.lgFitSystemId = String(detail.fitSystemId)
          input.dataset.lgFitSystemVersion = String(detail.fitSystemVersion)
          this.form.append(input)
        })

        if (recommended) {
          this.selectVariantOptions(recommended)
          this.updateVariant(recommended, { updateUrl: true, announce: false })
        }
        if (this.statusEl) this.statusEl.textContent = 'Fit selection applied. Review the selected size before adding to your bag.'
      }

      onFitInvalidated (event) {
        const detail = event.detail || {}
        if (!fitIdentityMatches(detail, this.hostedFitIdentity())) return
        const removed = removeMatchingFitProperties(this.form, detail)
        if (!removed) return

        // Do not dispatch a second variant event while the fit panel is in its
        // synchronous invalidation cycle. Re-render from the current controls.
        const selectedVariant = this.variantFromOptions()
        this.currentVariant = selectedVariant
        if (this.variantInput) this.variantInput.value = selectedVariant?.id || ''
        this.adjustPricing(selectedVariant)
        this.adjustButtons(selectedVariant)
        if (this.statusEl) this.statusEl.textContent = 'Fit selection removed. Review your selected size before adding to your bag.'
      }
    }

    if (!root.customElements.get('product-form')) root.customElements.define('product-form', ProductForm)

    class ProductStockIndicator extends root.HTMLElement {
      connectedCallback () {
        if (this.initialized) return
        this.initialized = true
        this.listener = event => this.update(event.detail)
        root.addEventListener('kt.product.variant.change', this.listener)
      }

      disconnectedCallback () {
        root.removeEventListener('kt.product.variant.change', this.listener)
        this.initialized = false
      }

      update (variant) {
        if (!variant) return
        const text = this.querySelector('.product-block-stock-indicator-text')
        const progress = this.querySelector('.progress')
        const bar = this.querySelector('.product-block-stock-indicator-bar')
        if (!text || !progress || !bar) return
        const locales = productLocales().stock_indicator || {}
        const threshold = positiveInteger(this.dataset.lowStockThreshold, 10)

        if (variant.inventory_management !== 'shopify') {
          text.textContent = locales.untracked_stock || 'Available'
          progress.setAttribute('hidden', '')
          return
        }

        progress.removeAttribute('hidden')
        if (!variant.available) {
          text.textContent = locales.no_stock || 'Sold out'
          bar.style.width = '0%'
          bar.setAttribute('aria-valuenow', '0')
          return
        }

        const quantity = Number(variant.inventory_quantity || 0)
        const percent = Math.max(Math.min((quantity / threshold) * 100, 100), 4)
        text.textContent = quantity <= threshold
          ? (locales.low_stock_other_html || 'Low stock').replace(/\d+/, quantity)
          : (locales.normal_stock_html || 'Available').replace(/\d+/, quantity)
        bar.style.width = `${percent}%`
        bar.setAttribute('aria-valuenow', String(Math.round(percent)))
      }
    }

    if (!root.customElements.get('product-stock-indicator')) {
      root.customElements.define('product-stock-indicator', ProductStockIndicator)
    }

    const adjustShopifySubscriptionsStyling = () => {
      document.querySelectorAll('.shopify_subscriptions_app_block [type="radio"]').forEach(radio => {
        radio.classList.add('form-check-input')
      })
    }
    adjustShopifySubscriptionsStyling()
    root.addEventListener('kt.product.quick_view.modal_shown', adjustShopifySubscriptionsStyling)
  }

  defineBrowserElements()

  return {
    ProductRequestError,
    positiveInteger,
    serializeLineItem,
    resolveVariant,
    pickInitialVariant,
    fitIdentityMatches,
    removeMatchingFitProperties,
    cartLineQuantity,
    fallbackAdd,
    withJsonSuffix
  }
})
